// utils/nativeAuth.js — Auth par TOKEN BEARER pour l'app native (Android).
//
// Pourquoi (audit Android 2026-07, sources officielles Capacitor/Android) : dans
// la WebView, la session par COOKIE tierce-partie fonctionne (Capacitor force
// setAcceptThirdPartyCookies) mais reste FRAGILE — elle dépend de la variable
// CSRF_COOKIE_DOMAIN côté serveur et de la survie des cookies tiers (dépréciation
// Chromium / CHIPS). Le backend accepte DÉJÀ le token en `Authorization: Bearer`
// (dependencies.get_current_user) et EXEMPTE le Bearer du contrôle CSRF. On stocke
// donc, EN NATIF UNIQUEMENT, le token renvoyé au login et on l'attache à chaque
// requête → auth indépendante des cookies. Le web reste 100 % inchangé (cookies).
//
// Le token est un session_token/access_token révocable et expirant (7 j), stocké
// via @capacitor/preferences (sandbox privé de l'app). Durcissement futur possible
// (Keystore chiffré) — cf. reco Capacitor Security.
import { safeLocalStorage } from "./safeStorage";

const KEY = "gively_native_auth_token";
let _token = null;      // cache mémoire (lecture SYNCHRONE dans l'intercepteur axios)
let _loaded = false;

export function isNativePlatform() {
  try {
    return !!(typeof window !== "undefined" && window.Capacitor
      && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch (_) { return false; }
}

// Charge le token persistant en mémoire au démarrage (idempotent). En natif on
// lit @capacitor/preferences ; sinon localStorage (utile pour ?debug/tests).
export async function loadNativeToken() {
  if (_loaded) return _token;
  _loaded = true;
  try {
    if (isNativePlatform()) {
      const { Preferences } = await import("@capacitor/preferences");
      const { value } = await Preferences.get({ key: KEY });
      _token = value || null;
    } else {
      _token = safeLocalStorage.getItem(KEY) || null;
    }
  } catch (_) { _token = null; }
  return _token;
}

// Lecture synchrone pour l'intercepteur axios (le cache mémoire est la source).
export function getNativeTokenSync() { return _token; }

// À appeler après un login réussi EN NATIF (email, Google natif, etc.).
export async function setNativeToken(token) {
  if (!isNativePlatform()) return;      // web : on ne stocke rien, cookies suffisent
  _token = token || null;
  _loaded = true;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    if (token) await Preferences.set({ key: KEY, value: token });
    else await Preferences.remove({ key: KEY });
  } catch (_) { /* best-effort */ }
}

export async function clearNativeToken() {
  _token = null;
  try {
    if (isNativePlatform()) {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.remove({ key: KEY });
    } else {
      safeLocalStorage.removeItem(KEY);
    }
  } catch (_) { /* noop */ }
}
